import { useEffect, useRef } from 'react';

import { mParticle } from '@local/analytics';
import type {
  IRoktSelection,
  RoktAttributes,
} from '@mparticle/web-sdk/internal';

import useEffectEvent from 'hooks/useEffectEvent';
import { logException } from 'helpers/loggers';

// `getConsentState` exists at runtime but isn't typed on `IMParticleUser`
// (same targeted-cast pattern as `useCookieConsentEvent.ts`).
type UserWithGetConsentState = {
  getConsentState: () =>
    | {
        getCCPAConsentState: () => { Consented: boolean } | null | undefined;
      }
    | null
    | undefined;
};

type UseShowRoktAdsParams = {
  /** Caller-composed gate — feature flag, platform, OneTrust readiness, etc. False keeps the hook idle. */
  isEnabled: boolean;
  identifier: string;
  attributes: RoktAttributes;
  onAdsLaunch?: () => void;
};

/**
 * Drives a single Rokt placement render through the mParticle kit. Waits for
 * `isEnabled` + a customer email, fires `selectPlacements` once inside
 * `mParticle.ready`, and closes the selection on unmount.
 */
export const useShowRoktAds = ({
  isEnabled,
  identifier,
  attributes,
  onAdsLaunch,
}: UseShowRoktAdsParams): void => {
  const selectionRef = useRef<IRoktSelection | undefined>();
  const hasLaunchedRef = useRef(false);
  const isUnmountedRef = useRef(false);

  const launchPlacement = useEffectEvent(async () => {
    const user = mParticle.Identity?.getCurrentUser?.() as
      | UserWithGetConsentState
      | null
      | undefined;

    // CCPA `Consented: true` = a data sale opt-out (diner opted out), per:
    // https://docs.mparticle.com/developers/client-sdks/web/data-privacy-controls/#setting-ccpa-opt-out
    const hasOptedOutOfSale =
      user?.getConsentState?.()?.getCCPAConsentState?.()?.Consented === true;
    if (hasOptedOutOfSale) {
      return;
    }

    onAdsLaunch?.();

    try {
      const selection = await mParticle.Rokt.selectPlacements({
        identifier,
        attributes,
      });

      if (isUnmountedRef.current) {
        selection.close();
      } else {
        selectionRef.current = selection;
      }
    } catch (error) {
      logException({
        message:
          error instanceof Error
            ? error
            : new Error('Rokt selectPlacements failed'),
        context: { identifier },
      });
    }
  });

  useEffect(() => {
    if (!isEnabled || hasLaunchedRef.current || !attributes.email) {
      return;
    }

    hasLaunchedRef.current = true;
    mParticle.ready(() => {
      void launchPlacement();
    });
  }, [isEnabled, attributes.email]);

  useEffect(
    () => () => {
      isUnmountedRef.current = true;
      selectionRef.current?.close();
    },
    []
  );
};
